import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SecurityIcon from '@mui/icons-material/Security';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WaterDamageIcon from '@mui/icons-material/WaterDamage';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';
import { emergencyService } from '@/services/emergencyService';
import type { EmergencyType } from '@/types/emergency';
import { useNavigate } from 'react-router-dom';

interface EmergencyTriggerModalProps {
  open: boolean;
  onClose: () => void;
  onEmergencyCreated?: (emergency: any) => void;
}

const EMERGENCY_TYPES: Array<{
  type: EmergencyType;
  label: string;
  icon: typeof LocalFireDepartmentIcon;
  color: string;
}> = [
  { type: 'FIRE', label: 'Fire', icon: LocalFireDepartmentIcon, color: '#EF4444' },
  { type: 'MEDICAL', label: 'Medical Emergency', icon: LocalHospitalIcon, color: '#3B82F6' },
  { type: 'SECURITY_THREAT', label: 'Security Threat', icon: SecurityIcon, color: '#F97316' },
  { type: 'ACCIDENT', label: 'Accident', icon: CarCrashIcon, color: '#EAB308' },
  { type: 'GAS_LEAK', label: 'Gas Leak', icon: WarningAmberIcon, color: '#EC4899' },
  { type: 'WATER_FLOODING', label: 'Water / Flooding', icon: WaterDamageIcon, color: '#06B6D4' },
  { type: 'POWER_ELECTRICAL', label: 'Power / Electrical', icon: FlashOnIcon, color: '#8B5CF6' },
  { type: 'CRIME_SUSPICIOUS', label: 'Suspicious Activity', icon: ReportProblemIcon, color: '#F43F5E' },
  { type: 'OTHER', label: 'Other Emergency', icon: NotificationsActiveIcon, color: '#64748B' },
];

export function EmergencyTriggerModal({ open, onClose, onEmergencyCreated }: EmergencyTriggerModalProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const showToast = useUiStore((s) => s.showToast);

  const [selectedType, setSelectedType] = useState<EmergencyType>('MEDICAL');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [confirmStep, setConfirmStep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Pre-fill user's house/block or gate if available
  useEffect(() => {
    if (open) {
      setConfirmStep(false);
      setErrorMessage('');
      setDescription('');
      setSelectedType('MEDICAL');

      const block = (user as any)?.blockName || (user as any)?.block || '';
      const flat = (user as any)?.flatNumber || (user as any)?.houseNumber || '';
      const role = ((user as any)?.role || user?.roles?.[0] || '').toUpperCase();

      if (block && flat) {
        setLocation(`${block} - ${flat}`);
      } else if (role === 'WATCHMAN') {
        setLocation('Main Security Gate');
      } else if (flat) {
        setLocation(`Flat ${flat}`);
      } else {
        setLocation('');
      }
    }
  }, [open, user]);

  const handleProceedToConfirm = () => {
    if (!location.trim()) {
      setErrorMessage('Please specify the emergency location.');
      return;
    }
    setErrorMessage('');
    setConfirmStep(true);
  };

  const handleSendEmergency = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const res = await emergencyService.raiseEmergency({
        emergencyType: selectedType,
        location: location.trim(),
        description: description.trim() || undefined,
      });

      showToast('🚨 Emergency alert broadcasted successfully across the Society!', 'error');
      onClose();
      if (onEmergencyCreated) {
        onEmergencyCreated(res.data);
      }
      if (res.data?._id) {
        navigate(`/emergencies/${res.data._id}`);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to broadcast emergency alert';
      setErrorMessage(msg);
      showToast(msg, 'error');
      setConfirmStep(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeTypeObj = EMERGENCY_TYPES.find((t) => t.type === selectedType) || EMERGENCY_TYPES[0];

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '2px solid',
          borderColor: 'error.main',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 20px 50px rgba(0, 0, 0, 0.8), 0 0 30px rgba(239, 68, 68, 0.25)'
              : '0 20px 40px rgba(239, 68, 68, 0.25)',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.18) : '#FEF2F2',
          borderBottom: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.3) : '#FEE2E2',
          py: 2,
          px: 3,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.3 : 0.15),
              color: 'error.main',
            }}
          >
            <LocalFireDepartmentIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{
                color: theme.palette.mode === 'dark' ? theme.palette.error.light : '#991B1B',
                lineHeight: 1.2,
              }}
            >
              {confirmStep ? '🚨 Confirm Emergency Alert' : '🚨 Raise Society Emergency'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.mode === 'dark' ? alpha(theme.palette.error.light, 0.85) : '#B91C1C',
                fontWeight: 500,
                display: 'block',
                mt: 0.25,
              }}
            >
              This will immediately notify ALL members across every Block in your Society.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, bgcolor: 'background.paper' }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {errorMessage}
          </Alert>
        )}

        {!confirmStep ? (
          <Stack spacing={2.5}>
            {/* Location Field */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: 'text.primary' }}>
                Emergency Location <Box component="span" sx={{ color: 'error.main' }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. B Block - 204, Club House, Main Gate"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <Typography variant="caption" color="text.secondary">
                Exact flat, tower, floor, or common area where the incident is occurring.
              </Typography>
            </Box>

            {/* Emergency Type Grid */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.primary' }}>
                Emergency Type <Box component="span" sx={{ color: 'error.main' }}>*</Box>
              </Typography>
              <Grid container spacing={1.5}>
                {EMERGENCY_TYPES.map((t) => {
                  const Icon = t.icon;
                  const isSelected = selectedType === t.type;
                  return (
                    <Grid item xs={6} sm={4} key={t.type}>
                      <Card
                        elevation={0}
                        sx={{
                          border: '2px solid',
                          borderColor: isSelected
                            ? t.color
                            : theme.palette.mode === 'dark'
                              ? alpha('#FFF', 0.12)
                              : 'divider',
                          bgcolor: isSelected
                            ? alpha(t.color, theme.palette.mode === 'dark' ? 0.22 : 0.1)
                            : theme.palette.mode === 'dark'
                              ? alpha('#FFF', 0.03)
                              : 'background.paper',
                          borderRadius: 1,
                          transition: 'all 0.15s ease-in-out',
                          '&:hover': {
                            borderColor: t.color,
                            bgcolor: alpha(t.color, theme.palette.mode === 'dark' ? 0.15 : 0.06),
                          },
                        }}
                      >
                        <CardActionArea onClick={() => setSelectedType(t.type)} sx={{ p: 1.25 }}>
                          <CardContent sx={{ p: 0, textAlign: 'center' }}>
                            <Icon sx={{ color: t.color, fontSize: 26, mb: 0.5 }} />
                            <Typography
                              variant="body2"
                              fontWeight={isSelected ? 800 : 600}
                              sx={{
                                color: isSelected ? t.color : 'text.primary',
                                fontSize: '0.8rem',
                                lineHeight: 1.2,
                              }}
                            >
                              {t.label}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Optional Description */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: 'text.primary' }}>
                Description / Instructions (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Briefly describe what happened or what assistance is needed..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Box>
          </Stack>
        ) : (
          /* Step 2: Confirmation Safeguard */
          <Stack spacing={2.5} sx={{ py: 1 }}>
            <Box
              sx={{
                p: 2.5,
                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.15) : '#FEF2F2',
                borderRadius: 2,
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.4) : '#FCA5A5',
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {activeTypeObj && <activeTypeObj.icon sx={{ color: activeTypeObj.color, fontSize: 32 }} />}
                  <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ color: activeTypeObj?.color || 'error.main' }}>
                      {activeTypeObj?.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                      Location: {location}
                    </Typography>
                  </Box>
                </Stack>
                {description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontStyle: 'italic',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 1.5,
                      borderRadius: 1,
                    }}
                  >
                    "{description}"
                  </Typography>
                )}
              </Stack>
            </Box>

            <Alert severity="warning" sx={{ fontWeight: 600 }}>
              Are you sure you want to broadcast this Society-wide alarm? An immediate high-priority emergency push notification will be sent to all residents and security personnel.
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: 'space-between',
          bgcolor: theme.palette.mode === 'dark' ? alpha('#FFF', 0.02) : '#F8FAFC',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button onClick={() => (confirmStep ? setConfirmStep(false) : onClose())} disabled={isSubmitting} color="inherit">
          {confirmStep ? 'Back' : 'Cancel'}
        </Button>

        {!confirmStep ? (
          <Button
            variant="contained"
            color="error"
            onClick={handleProceedToConfirm}
            sx={{
              fontWeight: 800,
              bgcolor: '#EF4444',
              px: 3,
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            Review & Send Alert
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            onClick={handleSendEmergency}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <LocalFireDepartmentIcon />}
            sx={{
              fontWeight: 900,
              bgcolor: '#DC2626',
              px: 3.5,
              py: 1,
              boxShadow: '0 4px 15px rgba(220, 38, 38, 0.4)',
              '&:hover': { bgcolor: '#B91C1C' },
            }}
          >
            {isSubmitting ? 'BROADCASTING...' : 'YES, BROADCAST EMERGENCY'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
